import { creditcardIcons } from '@/libs/components/src/lib/icons-creditcards';
import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { Input } from '@chiroup/components/form/Input';
import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import { ListClinic } from '@chiroup/core/types/Clinic.type';
import { Plan } from '@chiroup/core/types/Me.type';
import {
  Landmark as BuildingLibraryIcon,
  Check as CheckIcon,
  CreditCard as CreditCardIcon,
  Minus as MinusIcon,
} from 'lucide-react';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Fragment, use, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastContext, ToastTypes } from '../../../../contexts/toast.context';
import { useAdminPermissions } from '../../../../hooks/useAdminPermissions';
import Announcement from '../../../layout/Announcement';
import BillingSection from './BillingSection';
import BillingSectionMobile from './BillingSectionMobile';
import SubscriptionSummary from './SubscriptionSummary';

dayjs.extend(isSameOrAfter);

type Tier = {
  name: string;
  value: 'basic' | 'plus' | 'premium';
  price: { USA: number; CAN: number; ZAF: number; annual?: { USA: number; CAN: number } };
  description: string;
};

const tiers: Tier[] = [
  {
    name: 'Basic',
    value: 'basic',
    price: { USA: 149, CAN: 149, ZAF: 2399 },
    description: 'Every online resource ChiroUp has to offer',
  },
  {
    name: 'Plus',
    value: 'plus',
    price: { USA: 199, CAN: 199, ZAF: 3199 },
    description: 'Basic Features + social media posts',
  },
  {
    name: 'Premium',
    value: 'premium',
    price: { USA: 249, CAN: 249, ZAF: 3999 },
    description: 'Plus Features + community e-newsletters',
  },
];

// Standard (undiscounted) per-location prices; ZAR values mirror the backend's
// `zafPlans` (12578976635).
const standardAdditionalLocationPrice: Record<'USA' | 'CAN' | 'ZAF', number> = {
  USA: 99,
  CAN: 99,
  ZAF: 1599,
};

const sections = [
  {
    name: 'Online tools',
    features: [
      {
        name: 'Customizable, Condition-Specific Patient Reports with Online & Mobile Access',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: '“Best Practice” Protocols for 100+ Conditions (with Video Tutorials)',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Auto-Generated MD Initial & Release Summaries',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Automatic Google Reviews',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Clinic Dashboard with Key Practice Stats & Outcomes',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Professionally Designed Marketing Campaigns & Samples',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
      {
        name: 'Free Provider & Staff Training',
        tiers: { Basic: true, Plus: true, Premium: true },
      },
    ],
  },
  {
    name: 'Marketing',
    features: [
      {
        name: '20 Professionally Created, Patient-Centered Social Media Posts Every Month',
        tiers: { Basic: false, Plus: true, Premium: true },
      },
      {
        name: 'Automatic Social Posting',
        tiers: { Basic: false, Plus: true, Premium: true },
      },
    ],
  },
  {
    name: 'Clinical skills',
    features: [
      {
        name: 'Monthly Lay Newsletter',
        tiers: { Basic: false, Plus: false, Premium: true },
      },
      {
        name: 'Monthly MD Newsletter',
        tiers: { Basic: false, Plus: false, Premium: true },
      },
      {
        name: 'Quarterly Attorney Newsletter',
        tiers: { Basic: false, Plus: false, Premium: true },
      },
    ],
  },
];

const faqs = [
  {
    id: 1,
    question: 'What happens during my trial?',
    answer: `Getting your account started takes as little as 3 minutes! Once you complete the short sign-up process, you will have free access to ChiroUp’s platform for 14 days. You and your entire office will have the ability to utilize ChiroUp’s resources. No credit card is required. Your trial will include all of the Basic Plan features. For Plus or Premium add-ons, contact our sales team.`,
  },
  {
    id: 2,
    question: 'Can I try different plans during my trial?',
    answer:
      'Yes! If you would like to see samples of the Plus & Premium add-ons, contact our sales team for them to send samples to your inbox.',
  },
  {
    id: 3,
    question: 'Can I see a demo or speak with someone from the sales team?',
    answer: (
      <>
        Yes! ChiroUp is based in the St. Louis, MO metro area, and is in the Central Time Zone. Find
        a time to connect with our team in our calender link here:{' '}
        <a
          href="https://calendly.com/becki-4/30min"
          target="_blank"
          rel="noreferrer"
          className="text-primary-600 hover:text-primary-400"
        >
          Schedule a call now
        </a>
        . Don’t see a time that works for you? We want to work with you! Contact us for additional
        availability:{' '}
        <a href="mailto:support@chiroup.com" className="text-primary-600 hover:text-primary-400">
          support@chiroup.com
        </a>{' '}
        OR{' '}
        <a href="tel:8444624476" className="text-primary-600 hover:text-primary-400">
          (844) 462-4476
        </a>{' '}
        EXT. 1.
      </>
    ),
  },
  {
    id: 4,
    question: 'What happens after my trial ends?',
    answer:
      'Once your trial ends, if you decide that you want to continue to have access to ChiroUp (which we sure hope you do, 😎) you will select your subscription plan & plug in your payment information. You can do this directly from your ChiroUp account or by contacting your support call specialist.',
  },
];

export type ActionTypes = 'subscribe' | 'update';

type Props = {
  clinic: Partial<ListClinic>;
};

const Billing: React.FC<Props> = ({ clinic }) => {
  const [numberOfLocations, setNumberOfLocations] = useState(1);
  const { createToast } = use(ToastContext);
  const { hasPermission } = useAdminPermissions();
  const canEditBilling = hasPermission('clinic.edit_billing');
  const [selectedPlan, setSelectedPlan] = useState<Plan>();
  const [announcementText, setAnnouncementText] = useState<string>();
  const [confirmChangeModalState, setConfirmChangeModalState] = useState(OpenClosedStates.Closed);
  const [requiresAction, setRequiresAction] = useState(false);
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [extendTrial, setExtendTrial] = useState(false);
  const [noMoreTrials, setNoMoreTrials] = useState(false);
  const { state }: any = useLocation();

  useEffect(() => {
    if (clinic?.subscription?.locations) {
      setNumberOfLocations((clinic?.subscription?.locations || 0) + 1);
    }
  }, [clinic]);

  const callForHelp = useMemo(() => {
    return clinic?.bypassPaymentGateway || clinic?.freeAccount || clinic?.subscription?.callForHelp;
  }, [clinic?.bypassPaymentGateway, clinic?.freeAccount, clinic?.subscription?.callForHelp]);

  useEffect(() => {
    const billDate = clinic?.billDate ? dayjs(clinic?.billDate) : null;
    const trialEndDate = clinic?.trialEndDate ? dayjs(clinic?.trialEndDate) : null;
    const plan = clinic?.plan || 'basic';
    const stripeFailureMessage = clinic?.stripeFailureMessage;
    const freeAccount = clinic?.freeAccount;
    const requiresActionStripe =
      clinic?.subscription?.status === 'requires_action' ||
      clinic?.subscription?.status === 'incomplete';
    const billDateFormatted = billDate?.format('MM/DD/YYYY');
    const notActive =
      clinic?.subscription?.status !== 'active' && clinic?.subscription?.status !== 'trialing';
    const pastDue = clinic?.subscription?.status === 'past_due';
    const billDateHasNotPassed = dayjs(clinic?.billDate) > dayjs();
    const expiredText = notActive ? 'expired' : 'is set to expire';
    let billDateText = billDate ? ` on ${billDateFormatted}` : '';

    if (billDateHasNotPassed && notActive) {
      billDateText = '';
    }

    const billDateTextComplete = ` and your plan ${expiredText}${billDateText}`;

    if (stripeFailureMessage) {
      setAnnouncementText(stripeFailureMessage);
    } else if (requiresActionStripe && billDateHasNotPassed) {
      setAnnouncementText(
        `Look for microdeposits in your bank account and follow the instructions sent to the email you entered for billing to verify your payment method.`
      );
      setRequiresAction(true);
    } else if (plan === 'trial' && !freeAccount) {
      setAnnouncementText(
        `You are currently on a trial plan${billDateTextComplete}, please purchase a plan below to keep your account active.`
      );
    } else if (plan === 'education') {
      if (dayjs().isBefore(dayjs(billDateText))) {
        setAnnouncementText(
          `You are currently on an education plan${billDateTextComplete}. If you are no longer eligible to receive an education plan, please purchase a plan below to keep your account active.`
        );
      } else if (
        dayjs().isSameOrAfter(dayjs(billDateText)) &&
        clinic?.referralCode === 'RECENTGRAD'
      ) {
        setAnnouncementText(
          `Your education plan expired on ${billDateText}. You qualify for our special recent grad pricing! Please purchase a plan to keep your account active.`
        );
      }
    } else if (notActive) {
      setAnnouncementText(
        `Please update your subscription details below to continue using ChiroUp.`
      );
    } else {
      setAnnouncementText(undefined);
    }

    const twoWeeksAgo = dayjs().subtract(14, 'day');

    if (
      notActive &&
      !pastDue &&
      !requiresActionStripe &&
      (!trialEndDate || (trialEndDate && trialEndDate.isBefore(twoWeeksAgo))) &&
      typeof clinic?.trialCount === 'number'
    ) {
      if (clinic?.plan?.includes('education')) {
        setExtendTrial(false);
      } else if (clinic?.trialCount < 3) {
        setExtendTrial(true);
      } else if (clinic?.trialCount >= 3) {
        setExtendTrial(true);
        setNoMoreTrials(true);
      }
    }
  }, [
    clinic?.stripeFailureMessage,
    clinic?.plan,
    clinic?.billDate,
    clinic?.trialEndDate,
    clinic?.subscription,
    clinic?.trialCount,
    clinic?.freeAccount,
    clinic?.referralCode,
  ]);

  useEffect(() => {
    if (state?.plan) {
      const plan: Plan = state.plan;
      setSelectedPlan(plan);
    }
  }, [state?.plan]);

  const locationsHasChanged = useMemo(() => {
    const additionalLocationsPaidFor = clinic?.subscription?.locations || 0;

    return additionalLocationsPaidFor !== numberOfLocations - 1;
  }, [clinic, numberOfLocations]);

  // prices.currency is authoritative — the backend derives it from the primary
  // location's country, while Clinic.country (this component's `clinic.country`)
  // is never written at signup and stays 'USA' (12578976635).
  const priceKey: 'USA' | 'CAN' | 'ZAF' =
    clinic?.prices?.currency === 'ZAR'
      ? 'ZAF'
      : clinic?.country === 'CAN'
        ? 'CAN'
        : clinic?.country === 'ZAF'
          ? 'ZAF'
          : 'USA';

  const selectPlan = async (plan: Plan, type: ActionTypes = 'subscribe') => {
    setSelectedPlan(plan);

    if (type === 'update') {
      setConfirmChangeModalState(OpenClosedStates.Open);
    } else {
      // Make a call to get the stripe checkout page
      const res = await ChiroUpAPI.post('api', `/${clinic?.ID}/billing/create`, {
        body: {
          plan,
          locations: numberOfLocations - 1,
          promoCode: clinic?.referralCode,
        },
      });
      const url = res?.url;

      if (url) {
        window.location.href = url;
      }
    }
  };

  const closeConfirmChangeModal = () => {
    setConfirmChangeModalState(OpenClosedStates.Closed);
  };

  const confirmChange = async () => {
    setSavingUpdate(true);

    try {
      await ChiroUpAPI.post('api', `/${clinic?.ID}/billing/change`, {
        body: {
          plan: selectedPlan,
          locations: numberOfLocations - 1,
        },
      });
      window.location.reload();
    } catch (err: any) {
      let msg = '';

      if (err?.response?.data?.message === 'Cannot update annual plans.') {
        msg = 'Cannot update annual plans. Please contact support for further assistance.';
      } else {
        msg = 'Failed to change plans!';
      }

      setSavingUpdate(false);
      createToast({
        title: 'Error!',
        description: msg,
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }

    setSavingUpdate(false);
  };

  const onClickExtendTrial = async () => {
    if (!clinic?.ID) {
      return;
    }

    try {
      await ChiroUpAPI.post('api', `/${clinic.ID}/extend-trial`, {});
      window.location.reload();
    } catch (err: any) {
      createToast({
        title: 'Error!',
        description: err?.response?.data?.message ? (
          err.response.data.message
        ) : (
          <>Failed to extend trial!</>
        ),
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-dark-gray-900 dark:text-dark-gray-50">
        {clinic?.stripeFailureMessage ? (
          <Announcement
            text={announcementText || ''}
            icon={
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
            error
          />
        ) : noMoreTrials ? (
          <Announcement
            text="Your free trial eligibility has expired, but we'd still love to have you onboard! Please choose a plan to continue."
            icon={
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
          />
        ) : extendTrial ? (
          <Announcement
            text="Good news! You qualify for an additional 2 week trial."
            rightSide={
              canEditBilling ? (
                <Button
                  color={ButtonColors.plain}
                  className="text-primary-600"
                  text="Extend my trial"
                  onClick={onClickExtendTrial}
                />
              ) : undefined
            }
            icon={
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
          />
        ) : announcementText ? (
          <Announcement
            text={announcementText}
            icon={
              <svg
                className="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
          />
        ) : null}
        <div className="max-w-2xl mx-auto pt-16 sm:py-16 px-4 sm:px-6 lg:px-8 lg:max-w-7xl">
          <div className="px-0 sm:px-4 lg:px-0 flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
            <div className="max-w-xl">
              <h2 className="text-4xl font-extrabold text-primary-600 sm:text-5xl sm:tracking-tight lg:text-6xl">
                Billing
              </h2>
              <p className="mt-5 text-xl text-gray-500 dark:text-dark-gray-50">
                It’s Time to Take Your Practice to the Next Level. Get started in minutes. No
                contracts…ever.
              </p>
            </div>
            {clinic &&
              !clinic.bypassPaymentGateway &&
              !clinic.freeAccount &&
              clinic.stripePaymentMethod &&
              clinic.plan &&
              clinic.plan !== 'trial' && (
                <div className="flex flex-col gap-1">
                  <div
                    className="bg-gray-50 dark:bg-dark-gray-800 overflow-hidden sm:rounded-lg p-4 w-full sm:w-72"
                    style={{ minWidth: '18rem' }}
                  >
                    <dt className="font-medium text-gray-900 dark:text-white flex flex-row justify-between items-center gap-4">
                      <span>Payment information</span>
                      {canEditBilling && (
                        <Icon
                          icon={Icons.pencil}
                          className="w-4 h-4 text-gray-500 hover:text-gray-400 dark:text-dark-gray-400 dark:hover:text-dark-gray-300 cursor-pointer"
                          onClick={async () => {
                            const urlToGoTo = await ChiroUpAPI.post(
                              'api',
                              `/${clinic?.ID}/billing/update-payment-method`,
                              {}
                            );
                            window.location.href = urlToGoTo;
                          }}
                        />
                      )}
                    </dt>
                    <dd className="mt-2 space-y-2 sm:flex sm:space-y-0 sm:space-x-4 text-gray-900 dark:text-white">
                      {clinic.stripePaymentMethod?.type === 'card' && (
                        <>
                          <div className="flex-none">
                            {creditcardIcons[clinic.stripePaymentMethod?.brand]
                              ? creditcardIcons[clinic.stripePaymentMethod?.brand]
                              : creditcardIcons.default}
                          </div>
                          <div className="flex-auto">
                            <p>Ending with: {clinic.stripePaymentMethod.last4}</p>
                            <p>
                              Expires: {clinic.stripePaymentMethod.exp_month}/
                              {clinic.stripePaymentMethod.exp_year}
                            </p>
                          </div>
                        </>
                      )}
                      {clinic.stripePaymentMethod?.type === 'us_bank_account' && (
                        <>
                          <div className="flex-none">
                            <BuildingLibraryIcon className="h-6 w-auto text-gray-400 dark:text-dark-gray-300" />
                          </div>
                          <div className="flex-auto">
                            <p>{clinic.stripePaymentMethod?.bank_name || 'Bank'}</p>
                            <p>
                              Account #: XXXX
                              {clinic.stripePaymentMethod?.last4}
                            </p>
                          </div>
                        </>
                      )}
                    </dd>
                  </div>
                  {canEditBilling && (
                    <div
                      className="text-primary-500 cursor-pointer text-xs text-right"
                      onClick={async () => {
                        const urlToGoTo = await ChiroUpAPI.post(
                          'api',
                          `/${clinic?.ID}/billing/update-payment-method`,
                          {}
                        );
                        window.location.href = urlToGoTo;
                      }}
                    >
                      Billing details
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        <SubscriptionSummary clinic={clinic} />

        {callForHelp ? (
          <div className="max-w-2xl mx-auto py-6 sm:px-6 lg:max-w-7xl lg:px-8">
            <p className="mt-5 text-xl text-gray-500">
              Please contact us at 844.462.4476 ext. 2 if you'd like to change your plan.
            </p>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto py-6 sm:px-6 lg:max-w-7xl lg:px-8">
            <div className="flex flex-row gap-2 items-baseline justify-end mx-4 sm:mx-0">
              <span className="text-gray-900">Number of locations:</span>
              <Input
                type="number"
                name="locations"
                value={numberOfLocations}
                onChange={val => val && val > 0 && setNumberOfLocations(val)}
                className="mb-12 w-32"
                min="1"
              />
            </div>
            <div className="space-y-6 h-full lg:hidden divide-y divide-gray-300">
              {tiers.map(tier => (
                <BillingSectionMobile
                  key={tier.value}
                  name={tier.name}
                  description={tier.description}
                  standardPrice={tier.price?.[priceKey]}
                  additionalLocationsPrice={clinic?.prices?.additionalLocations}
                  standardAdditionalLocationsPrice={standardAdditionalLocationPrice[priceKey]}
                  clinicCountry={clinic?.country}
                  currency={priceKey === 'ZAF' ? 'ZAR' : 'USD'}
                  selectPlan={selectPlan}
                  value={tier.value}
                  currentPlan={clinic?.subscription?.plan || clinic?.plan}
                  subscriptionPlanPrice={clinic?.prices?.[tier.value]}
                  status={clinic?.subscription?.status}
                  disabled={
                    !canEditBilling ||
                    (!!clinic?.stripeFailureMessage && !!clinic?.stripePaymentMethod) ||
                    requiresAction
                  }
                  coupon={clinic?.prices?.coupon}
                  additionalLocations={numberOfLocations - 1}
                  locationsHasChanged={locationsHasChanged}
                  appliesToEverything={clinic?.prices?.coupon?.appliesToEverything}
                />
              ))}
            </div>
            <div className="hidden lg:block">
              <table className="w-full h-px table-fixed">
                <thead>
                  <tr>
                    <th
                      className="pb-4 pl-6 pr-6 text-sm font-medium text-gray-900 dark:text-dark-gray-50 text-left"
                      scope="col"
                    >
                      <span>Plans</span>
                    </th>
                    {tiers.map(tier => (
                      <th
                        key={tier.name}
                        className="w-1/4 pb-4 px-6 text-lg leading-6 font-medium text-gray-900 dark:text-dark-gray-50 text-left"
                        scope="col"
                      >
                        {tier.name}{' '}
                        {clinic?.subscription?.status === 'active' &&
                          clinic?.plan === tier.value && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-accent-100 text-accent-500">
                              Current plan
                            </span>
                          )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="border-t border-gray-300 dark:border-dark-gray-800 divide-y divide-gray-300 dark:divide-dark-gray-800">
                  <tr>
                    <th
                      className="py-8 pl-6 pr-6 align-top text-sm font-medium text-gray-900 dark:text-dark-gray-50 text-left"
                      scope="row"
                    >
                      <span className="flex flex-col">
                        <span>Pricing</span>
                        <span className="text-gray-500 italic text-xs font-light">
                          Sales Tax may apply
                        </span>
                      </span>
                    </th>
                    {tiers.map(tier => (
                      <BillingSection
                        key={tier.value}
                        name={tier.name}
                        description={tier.description}
                        standardPrice={tier.price?.[priceKey]}
                        additionalLocationsPrice={clinic?.prices?.additionalLocations}
                        standardAdditionalLocationsPrice={standardAdditionalLocationPrice[priceKey]}
                        clinicCountry={clinic?.country}
                        currency={priceKey === 'ZAF' ? 'ZAR' : 'USD'}
                        selectPlan={selectPlan}
                        value={tier.value}
                        currentPlan={clinic?.subscription?.plan || clinic?.plan}
                        subscriptionPlanPrice={clinic?.prices?.[tier.value]}
                        status={clinic?.subscription?.status}
                        disabled={
                          !canEditBilling ||
                          (!!clinic?.stripeFailureMessage && !!clinic?.stripePaymentMethod) ||
                          requiresAction
                        }
                        coupon={clinic?.prices?.coupon}
                        additionalLocations={numberOfLocations - 1}
                        locationsHasChanged={locationsHasChanged}
                        appliesToEverything={clinic?.prices?.coupon?.appliesToEverything}
                      />
                    ))}
                  </tr>

                  {sections.map(section => (
                    <Fragment key={section.name}>
                      <tr>
                        <th
                          className="py-3 pl-6 bg-gray-50 dark:bg-dark-gray-900 text-sm font-medium text-gray-900 dark:text-dark-gray-50 text-left"
                          colSpan={4}
                          scope="colgroup"
                        >
                          {section.name}
                        </th>
                      </tr>
                      {section.features.map((feature: any) => (
                        <tr key={feature.name}>
                          <th
                            className="py-5 pl-6 pr-6 text-sm font-normal text-gray-500 text-left"
                            scope="row"
                            colSpan={1}
                          >
                            {feature.name}
                          </th>
                          {tiers.map(tier => (
                            <td key={tier.name} className="py-5 px-6" colSpan={1}>
                              {typeof feature.tiers[tier.name] === 'string' ? (
                                <span className="block text-sm text-gray-700">
                                  {feature.tiers[tier.name]}
                                </span>
                              ) : feature.tiers[tier.name] === true ? (
                                <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
                              ) : (
                                <MinusIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-20 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="space-y-4">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-dark-gray-50">
                Frequently asked questions
              </h2>
              <p className="text-lg text-gray-500">
                Can’t find the answer you’re looking for? Reach out to our{' '}
                <a
                  href="mailto:support@chiroup.com"
                  className="font-medium text-primary-600 hover:text-primary-400"
                >
                  customer support
                </a>{' '}
                team.
              </p>
            </div>
            <div className="mt-12 lg:mt-0 lg:col-span-2">
              <dl className="space-y-12">
                {faqs.map(faq => (
                  <div key={faq.id}>
                    <dt className="text-lg leading-6 font-medium text-gray-900 dark:text-dark-gray-50">
                      {faq.question}
                    </dt>
                    <dd className="mt-2 text-base text-gray-500">{faq.answer}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmChangeModalState === OpenClosedStates.Open}
        confirm={confirmChange}
        close={closeConfirmChangeModal}
        confirmText="Change plan"
        description={
          locationsHasChanged && selectedPlan === clinic?.subscription?.plan
            ? `Are you sure you would like to change the number of locations for your account? You will be billed the new amount on your next billing cycle.`
            : `Are you sure you would like to change your plan to ${selectedPlan}? You will be billed the new amount on your next billing cycle.`
        }
        icon={<CreditCardIcon className="h-6 w-6 text-primary-500" aria-hidden="true" />}
        loading={savingUpdate}
      />
    </>
  );
};

export default Billing;
